import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  TextField,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Box,
  Alert,
  Divider
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EmailIcon from '@mui/icons-material/Email';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

import { useActionSettings } from '../../hooks/useActionSettings';
import { useWebSocket } from '../WebSocketContext';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagsDropdown from '../TagsDropdown';
import TagEditor from '../Widgets/TagEditor';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { useFolderPicker } from '../useFolderPicker';
import { buildGetExportSettingsPayload } from '../../Utils/actionSettingsUtils';

const createRecipient = () => ({
  id: `rcp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  type: 'to',
  value: ''
});

const normalizeRecipients = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [createRecipient()];
  }

  return items.map((recipient) => ({
    ...recipient,
    id: recipient.id || createRecipient().id,
    type: recipient.type || 'to',
    value: recipient.value || ''
  }));
};

const serializeRecipients = (list) => {
  if (!Array.isArray(list)) return '[]';
  return JSON.stringify(
    list.map((recipient) => ({
      type: recipient.type || 'to',
      value: recipient.value || ''
    }))
  );
};

const createAttachment = () => ({
  id: `att_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  path: '',
  name: ''
});

const normalizeAttachments = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  return items.map((attachment) => {
    if (typeof attachment === 'string') {
      return {
        id: createAttachment().id,
        path: attachment,
        name: ''
      };
    }
    if (!attachment || typeof attachment !== 'object') {
      return createAttachment();
    }
    return {
      id: attachment.id || createAttachment().id,
      path: attachment.path || '',
      name: attachment.name || ''
    };
  });
};

const serializeAttachments = (list) => {
  if (!Array.isArray(list)) return '[]';
  return JSON.stringify(
    list
      .map((attachment) => ({
        path: (attachment.path || '').trim(),
        name: (attachment.name || '').trim()
      }))
      .filter((attachment) => attachment.path)
  );
};

const createTagAssignment = () => ({
  id: `tagassign_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  tag: '',
  valueType: 'true'
});

const ActionsOutlookSendEmail = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();

  const [accountEmail, setAccountEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isHtmlBody, setIsHtmlBody] = useState(true);
  const [fromName, setFromName] = useState('');
  const [saveToSentItems, setSaveToSentItems] = useState(true);
  const [createDraft, setCreateDraft] = useState(false);
  const [recipients, setRecipients] = useState([createRecipient()]);
  const [attachments, setAttachments] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected | pending | connected
  const [connectionMessage, setConnectionMessage] = useState('');
  const [pendingDisconnect, setPendingDisconnect] = useState(false);
  const [verified, setVerified] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [attachmentBrowseDisabled, setAttachmentBrowseDisabled] = useState(false);
  const [attachmentBrowseIndex, setAttachmentBrowseIndex] = useState(null);
  const [tagMenuAnchor, setTagMenuAnchor] = useState(null);
  const [tagTarget, setTagTarget] = useState(null);
  const [tagAssignments, setTagAssignments] = useState([]);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);
  const subjectRef = useRef(null);
  const fromNameRef = useRef(null);
  const bodyRef = useRef(null);
  const { handleBrowseDirectory: openAttachmentBrowser, FolderBrowserComponent: AttachmentBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      if (selectedPath && attachmentBrowseIndex !== null) {
        setAttachments((prev) => prev.map((attachment, index) => {
          if (index !== attachmentBrowseIndex) return attachment;
          return { ...attachment, path: selectedPath };
        }));
      }
      setAttachmentBrowseIndex(null);
      setAttachmentBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setAttachmentBrowseIndex(null);
      setAttachmentBrowseDisabled(false);
    }
  });

  const customComparisonCallback = useCallback((initial = {}) => {
    const initialAuth = initial.outlookAuth || {};
    const initialRecipients = serializeRecipients(initial.recipients || []);
    const currentRecipients = serializeRecipients(recipients);
    const initialAttachments = serializeAttachments(initial.attachments || []);
    const currentAttachments = serializeAttachments(attachments);
    const initialAssignments = JSON.stringify(initial.tagAssignments || []);
    const currentAssignments = JSON.stringify(
      tagAssignments.map(({ tag, valueType }) => ({
        tag: (tag || '').trim(),
        valueType: valueType || 'true'
      }))
    );

    return (
      subject === ((initial.template && initial.template.subject) || '') &&
      body === ((initial.template && initial.template.body) || '') &&
      isHtmlBody === ((initial.template && initial.template.isHtml) !== undefined ? initial.template.isHtml : true) &&
      fromName === (initial.fromName || '') &&
      saveToSentItems === (initial.saveToSentItems !== undefined ? initial.saveToSentItems : true) &&
      createDraft === (initial.createDraft !== undefined ? initial.createDraft : false) &&
      accountEmail === (initialAuth.account || '') &&
      verified === !!initialAuth.verified &&
      pendingDisconnect === false &&
      currentRecipients === initialRecipients &&
      currentAttachments === initialAttachments &&
      currentAssignments === initialAssignments
    );
  }, [subject, body, isHtmlBody, fromName, saveToSentItems, createDraft, accountEmail, verified, pendingDisconnect, recipients, attachments, tagAssignments]);

  const customSaveDataCallback = useCallback(() => {
    const serializedRecipients = recipients.map(({ id, ...rest }) => rest);
    const serializedAttachments = attachments
      .map(({ id, ...rest }) => ({
        path: (rest.path || '').trim(),
        name: (rest.name || '').trim()
      }))
      .filter((attachment) => attachment.path);
    const sanitizedAssignments = tagAssignments
      .filter((assignment) => assignment.tag && assignment.tag.trim())
      .map((assignment) => ({
        tag: assignment.tag.trim(),
        valueType: assignment.valueType || 'true'
      }));
    const outlookAuth = {
      account: accountEmail,
      verified,
      ...(pendingDisconnect ? { disconnect: true } : {})
    };

    return {
      fromName,
      saveToSentItems,
      createDraft,
      template: {
        subject,
        body,
        isHtml: isHtmlBody
      },
      recipients: serializedRecipients,
      attachments: serializedAttachments,
      outlookAuth,
      tagAssignments: sanitizedAssignments
    };
  }, [fromName, saveToSentItems, createDraft, subject, body, isHtmlBody, recipients, attachments, accountEmail, verified, pendingDisconnect, tagAssignments]);

  const loadDataCallback = useCallback((response) => {
    if (!response?.customData) return;
    const data = response.customData;
    const template = data.template || {};
    const outlookAuth = data.outlookAuth || {};

    setSubject(template.subject || '');
    setBody(template.body || '');
    setIsHtmlBody(template.isHtml !== undefined ? template.isHtml : true);
    setFromName(data.fromName || '');
    setSaveToSentItems(data.saveToSentItems !== undefined ? data.saveToSentItems : true);
    setCreateDraft(data.createDraft !== undefined ? data.createDraft : false);
    setRecipients(normalizeRecipients(data.recipients));
    setAttachments(normalizeAttachments(data.attachments));
    setAccountEmail(outlookAuth.account || outlookAuth.preferredUsername || '');
    setVerified(!!outlookAuth.verified);
    setPendingDisconnect(false);
    const savedAssignments = (data.tagAssignments || data.postSendTagUpdates || []).map((assignment, idx) => ({
      id: assignment.id || `tagassign_${idx}_${Date.now()}`,
      tag: assignment.tag || '',
      valueType: assignment.valueType || 'true'
    }));
    setTagAssignments(savedAssignments);

    if (outlookAuth.pendingState) {
      setConnectionStatus('pending');
      setConnectionMessage(t('workflow.outlook_send_email.awaiting_auth', 'Complete the Microsoft login flow in the opened tab.'));
    } else if (outlookAuth.verified) {
      setConnectionStatus('connected');
      setConnectionMessage(t('workflow.outlook_send_email.connected', 'Connected to Outlook account.'));
    } else {
      setConnectionStatus('disconnected');
      setConnectionMessage('');
    }
  }, [t]);

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    id
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'sendOutlookEmail',
    onSaveStatusChange,
    customComparison: customComparisonCallback,
    customSaveData: customSaveDataCallback,
    loadDataFunction: loadDataCallback
  });

  const isPersistedAction = useMemo(() => (
    id && !(typeof id === 'string' && id.startsWith('temp_'))
  ), [id]);

  const handleTagMenuOpen = (event, target) => {
    setTagTarget(target);
    setTagMenuAnchor(event.currentTarget);
  };

  const handleTagSelect = (tagValue) => {
    if (!tagTarget) {
      setTagMenuAnchor(null);
      return;
    }

    if (tagTarget === 'subject' && subjectRef.current) {
      subjectRef.current.insertTag(tagValue);
    } else if (tagTarget === 'body' && bodyRef.current) {
      bodyRef.current.insertTag(tagValue);
    } else if (tagTarget === 'fromName' && fromNameRef.current) {
      fromNameRef.current.insertTag(tagValue);
    } else if (typeof tagTarget === 'object' && tagTarget.type === 'recipient') {
      const token = `{${tagValue}}`;
      setRecipients((prev) => prev.map((recipient, index) => {
        if (index === tagTarget.index) {
          return { ...recipient, value: `${recipient.value}${token}` };
        }
        return recipient;
      }));
    } else if (typeof tagTarget === 'object' && tagTarget.type === 'attachment') {
      const token = `{${tagValue}}`;
      const field = tagTarget.field || 'path';
      setAttachments((prev) => prev.map((attachment, index) => {
        if (index === tagTarget.index) {
          return { ...attachment, [field]: `${attachment[field] || ''}${token}` };
        }
        return attachment;
      }));
    }

    setTagMenuAnchor(null);
    setTagTarget(null);
  };

  const handleRecipientChange = (index, field, value) => {
    setRecipients((prev) => prev.map((recipient, idx) => {
      if (idx !== index) return recipient;
      return { ...recipient, [field]: value };
    }));
  };

  const addRecipient = () => {
    setRecipients((prev) => [...prev, createRecipient()]);
  };

  const removeRecipient = (index) => {
    setRecipients((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const handleAttachmentChange = (index, field, value) => {
    setAttachments((prev) => prev.map((attachment, idx) => {
      if (idx !== index) return attachment;
      return { ...attachment, [field]: value };
    }));
  };

  const addAttachment = () => {
    setAttachments((prev) => [...prev, createAttachment()]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleBrowseAttachment = (index) => {
    setAttachmentBrowseIndex(index);
    setAttachmentBrowseDisabled(true);
    openAttachmentBrowser();
  };

  const handleConnect = () => {
    if (!ws) return;
    if (!isPersistedAction) {
      setConnectionMessage(t('workflow.outlook_send_email.save_before_connect', 'Save the action before connecting Outlook.'));
      return;
    }

    if (!accountEmail) {
      setConnectionMessage(t('workflow.outlook_send_email.enter_account', 'Enter your Outlook email before connecting.'));
      return;
    }

    setIsAuthorizing(true);
    setConnectionMessage('');
    setPendingDisconnect(false);
    let returnUrl = window.location.href;
    try {
      const url = new URL(returnUrl);
      url.searchParams.set('openActions', 'true');
      url.searchParams.set('exportId', String(id));
      returnUrl = url.toString();
    } catch (err) {
      // keep original returnUrl
    }
    ws.send(JSON.stringify({
      command: 'authorizeOutlookAction',
      caseid,
      exportId: id,
      account: accountEmail,
      returnUrl
    }));
  };

  const handleDisconnect = () => {
    setConnectionStatus('disconnected');
    setConnectionMessage(t('workflow.outlook_send_email.disconnect_pending', 'The connection will be removed after you save these settings.'));
    setPendingDisconnect(true);
    setVerified(false);
  };

  useEffect(() => {
    const unsubscribe = subscribeToMessages((message) => {
      if (!message) return;

      if (message.command === 'authorizeOutlookActionStart') {
        const payload = message.data || {};
        if (payload.error) {
          setConnectionMessage(payload.error);
          setIsAuthorizing(false);
          return;
        }
        if (payload.exportId === id) {
          if (payload.url) {
            window.location.assign(payload.url);
          }
          setConnectionStatus('pending');
          setConnectionMessage(t('workflow.outlook_send_email.awaiting_auth', 'Complete the Microsoft login flow in the opened tab.'));
          setIsAuthorizing(false);
        }
      } else if (message.command === 'outlookActionTokenResult' && (message.data?.exportId === id)) {
        const payload = message.data || {};
        if (payload.verified) {
          setConnectionStatus('connected');
          setConnectionMessage(t('workflow.outlook_send_email.connected', 'Connected to Outlook account.'));
          setVerified(true);
          setPendingDisconnect(false);
          if (isPersistedAction) {
            sendMessage(buildGetExportSettingsPayload(id, caseid));
          }
        } else if (payload.error) {
          setConnectionStatus('disconnected');
          setConnectionMessage(payload.error);
        }
      } else if (message.command === 'verifyOutlookActionTokenResult' && (message.data?.exportId === id)) {
        const payload = message.data || {};
        setIsCheckingConnection(false);
        if (payload.verified) {
          setConnectionStatus('connected');
          setConnectionMessage(t('workflow.outlook_send_email.connected', 'Connected to Outlook account.'));
          setVerified(true);
          setPendingDisconnect(false);
        } else {
          setConnectionStatus('disconnected');
          setConnectionMessage(payload.error || t('workflow.outlook_send_email.invalid_connection', 'Outlook connection requires re-authentication.'));
          setVerified(false);
        }
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, id, t, ws, isPersistedAction, sendMessage]);

  useEffect(() => {
    if (!ws || !isPersistedAction) {
      return;
    }
    setIsCheckingConnection(true);
    setConnectionStatus('pending');
    setConnectionMessage(t('workflow.outlook_send_email.checking_status', 'Checking Outlook connection...'));
    ws.send(JSON.stringify({
      command: 'verifyOutlookActionToken',
      caseid,
      exportId: id
    }));
  }, [ws, caseid, id, isPersistedAction, t]);

  const connectionChipColor = connectionStatus === 'connected'
    ? 'success'
    : connectionStatus === 'pending' || isCheckingConnection
      ? 'warning'
      : 'default';

  const connectionAlertSeverity = connectionStatus === 'connected'
    ? 'success'
    : connectionStatus === 'disconnected'
      ? 'error'
      : 'info';

  return (
    <>
      <ActionSettingsLayout
        isEnabled={isEnabled}
        onEnabledChange={setIsEnabled}
        title={t('workflow.send_outlook_email.title', 'Send email (Outlook)')}
        isSaveDisabled={isSaveDisabled}
        onSave={handleSave}
        onForceRun={handleForceRun}
        forceRunLoading={forceRunLoading}
        conditions={conditions}
        onConditionsChange={handleConditionsChange}
        tags={tags}
        popUpOpen={popUpOpen}
        forceRunResult={forceRunResult}
        onCloseStatusWindow={handleCloseStatusWindow}
        forceRunWarningOpen={forceRunWarningOpen}
        forceRunWarningCount={forceRunWarningCount}
        onForceRunConfirm={handleForceRunConfirm}
        onForceRunCancel={handleForceRunCancel}
        primaryColor="#4a6df0"
      >
        <>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2} gap={2}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {t('workflow.outlook_send_email.connection', 'Outlook connection')}
                  </Typography>
                  <Chip
                    size="small"
                    color={connectionChipColor}
                    label={connectionStatus === 'connected'
                      ? t('workflow.outlook_send_email.status_connected', 'Connected')
                      : connectionStatus === 'pending'
                        ? t('workflow.outlook_send_email.status_pending', 'Pending')
                        : t('workflow.outlook_send_email.status_disconnected', 'Disconnected')}
                  />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('workflow.outlook_send_email.account_email', 'Outlook email')}
                      value={accountEmail}
                      onChange={(e) => setAccountEmail(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6} display="flex" alignItems="center" gap={2}>
                    <Button
                      variant="contained"
                      startIcon={<LinkIcon />}
                      onClick={handleConnect}
                      disabled={isAuthorizing || isCheckingConnection || connectionStatus === 'connected'}
                    >
                      {t('workflow.outlook_send_email.connect', 'Connect Outlook')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<LinkOffIcon />}
                      onClick={handleDisconnect}
                      disabled={connectionStatus === 'disconnected'}
                    >
                      {t('workflow.outlook_send_email.disconnect', 'Disconnect')}
                    </Button>
                  </Grid>
                  {connectionMessage && (
                    <Grid item xs={12}>
                      <Alert severity={connectionAlertSeverity}>
                        {connectionMessage}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
              {t('workflow.outlook_send_email.subject', 'Email subject')}
            </Typography>
            <Box sx={{
              borderRadius: '12px',
              backgroundColor: '#fff',
              overflow: 'hidden',
              '&:hover': { borderColor: '#5757b0' },
            }}>
              <TagEditor
                    caseid={caseid}
                ref={subjectRef}
                value={subject}
                onChange={setSubject}
                placeholder={t('workflow.outlook_send_email.subject', 'Email subject')}
                singleLine
                onTagClick={(e) => handleTagMenuOpen(e, 'subject')}
                tags={tags}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
              {t('workflow.outlook_send_email.from_name', 'From display name')}
            </Typography>
            <Box sx={{
              borderRadius: '12px',
              backgroundColor: '#fff',
              overflow: 'hidden',
              '&:hover': { borderColor: '#5757b0' },
            }}>
              <TagEditor
                    caseid={caseid}
                ref={fromNameRef}
                value={fromName}
                onChange={setFromName}
                placeholder={t('workflow.outlook_send_email.from_name', 'From display name')}
                singleLine
                onTagClick={(e) => handleTagMenuOpen(e, 'fromName')}
                tags={tags}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ mr: 2 }}>
                {t('workflow.outlook_send_email.body', 'Email body')}
              </Typography>
            </Box>
            <Box sx={{
              borderRadius: '12px',
              backgroundColor: '#fff',
              minHeight: 200,
              overflow: 'hidden',
              '&:hover': { borderColor: '#5757b0' },
            }}>
              <TagEditor
                    caseid={caseid}
                ref={bodyRef}
                value={body}
                onChange={setBody}
                placeholder={t('workflow.outlook_send_email.body', 'Email body')}
                minRows={6}
                onTagClick={(e) => handleTagMenuOpen(e, 'body')}
                tags={tags}
              />
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isHtmlBody}
                    onChange={(e) => setIsHtmlBody(e.target.checked)}
                  />
                }
                label={t('workflow.outlook_send_email.html_body', 'Send HTML email')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={saveToSentItems}
                    onChange={(e) => setSaveToSentItems(e.target.checked)}
                    disabled={createDraft}
                  />
                }
                label={t('workflow.outlook_send_email.save_to_sent', 'Save to Sent Items')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={createDraft}
                    onChange={(e) => setCreateDraft(e.target.checked)}
                  />
                }
                label={t('workflow.outlook_send_email.create_draft_only', 'Create Draft Only')}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {t('workflow.outlook_send_email.recipients', 'Recipients')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={addRecipient}
                  >
                    {t('workflow.outlook_send_email.add_recipient', 'Add recipient')}
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {recipients.map((recipient, index) => (
                  <Box
                    key={recipient.id}
                    mb={2}
                    p={2}
                    borderRadius={2}
                    border="1px solid #ececec"
                    display="flex"
                    flexWrap="wrap"
                    gap={2}
                    alignItems="center"
                  >
                    <FormControl
                      sx={{
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': { height: 56 }
                      }}
                    >
                      <InputLabel>{t('workflow.outlook_send_email.recipient_type', 'Type')}</InputLabel>
                      <Select
                        label={t('workflow.outlook_send_email.recipient_type', 'Type')}
                        value={recipient.type}
                        onChange={(e) => handleRecipientChange(index, 'type', e.target.value)}
                      >
                        <MenuItem value="to">{t('workflow.outlook_send_email.to', 'To')}</MenuItem>
                        <MenuItem value="cc">{t('workflow.outlook_send_email.cc', 'Cc')}</MenuItem>
                        <MenuItem value="bcc">{t('workflow.outlook_send_email.bcc', 'Bcc')}</MenuItem>
                      </Select>
                    </FormControl>

                    <Box sx={{ flex: 1, borderRadius: '8px', overflow: 'hidden' }}>
                      <TagEditor
                    caseid={caseid}
                        value={recipient.value}
                        onChange={(val) => handleRecipientChange(index, 'value', val)}
                        placeholder={t('workflow.outlook_send_email.email_address', 'Email address')}
                        minHeight="35px"
                        singleLine
                        tags={tags}
                        onTagClick={(e) => handleTagMenuOpen(e, { type: 'recipient', index })}
                      />
                    </Box>

                    <IconButton
                      color="error"
                      disabled={recipients.length === 1}
                      onClick={() => removeRecipient(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AttachFileIcon fontSize="small" />
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {t('workflow.outlook_send_email.attachments', 'Attachments')}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={addAttachment}
                  >
                    {t('workflow.outlook_send_email.add_attachment', 'Add attachment')}
                  </Button>
                </Box>
                {attachments.length === 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {t('workflow.outlook_send_email.attachments_help', 'Attach local files or use tags in file paths.')}
                  </Typography>
                )}
                {attachments.length > 0 && <Divider sx={{ mb: 2 }} />}
                {attachments.map((attachment, index) => (
                  <Box
                    key={attachment.id}
                    mb={2}
                    p={2}
                    borderRadius={2}
                    border="1px solid #ececec"
                    display="flex"
                    flexWrap="wrap"
                    gap={2}
                    alignItems="center"
                  >
                    <Box sx={{ flex: 1.5, minWidth: { xs: '100%', md: 320 } }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                        {t('workflow.outlook_send_email.attachment_path', 'Attachment path')}
                      </Typography>
                      <Box sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                        <TagEditor
                    caseid={caseid}
                          value={attachment.path}
                          onChange={(value) => handleAttachmentChange(index, 'path', value)}
                          placeholder={t('workflow.outlook_send_email.attachment_path_placeholder', '/folder/document.pdf')}
                          singleLine
                          tags={tags}
                          onTagClick={(e) => handleTagMenuOpen(e, { type: 'attachment', field: 'path', index })}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 220 } }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                        {t('workflow.outlook_send_email.attachment_name', 'Attachment name (optional)')}
                      </Typography>
                      <Box sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                        <TagEditor
                    caseid={caseid}
                          value={attachment.name}
                          onChange={(value) => handleAttachmentChange(index, 'name', value)}
                          placeholder={t('workflow.outlook_send_email.attachment_name_placeholder', 'invoice.pdf')}
                          singleLine
                          tags={tags}
                          onTagClick={(e) => handleTagMenuOpen(e, { type: 'attachment', field: 'name', index })}
                        />
                      </Box>
                    </Box>

                    <Button
                      variant="outlined"
                      startIcon={<FolderOpenIcon />}
                      onClick={() => handleBrowseAttachment(index)}
                      disabled={attachmentBrowseDisabled}
                    >
                      {t('common.browse', 'Browse')}
                    </Button>

                    <IconButton
                      color="error"
                      onClick={() => removeAttachment(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Alert icon={<EmailIcon />} severity="info">
              {t('workflow.outlook_send_email.tag_hint', 'Use tags such as {customer_email} to personalize subjects, bodies, or recipients.')}
            </Alert>
          </Grid>
        </>
      </ActionSettingsLayout>

      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={tagMenuAnchor}
        setAnchorEl={setTagMenuAnchor}
      />
      {AttachmentBrowserComponent}
    </>
  );
};

export default ActionsOutlookSendEmail;

